import { type FetcherWithComponents, Form, useNavigation } from "react-router";
import { useEffect, useRef, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { CSRFToken } from "~/lib/csrf";

type Props = {
  action?: string;
  fetcher?: FetcherWithComponents<any>;
  title?: string;
  description?: string;
  subaction?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  idField?: string;
  idFieldValue?: string;
  cancelButtonText?: string;
  continueButtonText?: string;
  submittingButtonText?: string;
  otherInputs?: Record<string, string>;
  requireConfirmationText?: string;
};

export default function ConfirmDelete({
  action,
  fetcher,
  idField,
  idFieldValue,
  title = "Confirm delete",
  description = "Are you sure you want to delete this?",
  subaction = "delete",
  open,
  setOpen,
  cancelButtonText = "Cancel",
  continueButtonText = "Continue",
  submittingButtonText = "Deleting...",
  otherInputs = {},
  requireConfirmationText,
}: Props) {
  const navigation = useNavigation();
  const FormComponent = fetcher ? fetcher.Form : Form;

  const submissionState = fetcher ? fetcher.state : navigation.state;
  const isSubmitting = submissionState === "submitting";
  const wasSubmitting = useRef(false);
  const [confirmationInput, setConfirmationInput] = useState("");

  useEffect(() => {
    if (!open) {
      setConfirmationInput("");
    }
  }, [open]);

  useEffect(() => {
    if (submissionState === "submitting") {
      wasSubmitting.current = true;
    }

    if (wasSubmitting.current && submissionState === "idle") {
      wasSubmitting.current = false;
      setOpen(false);
    }
  }, [submissionState, setOpen]);

  const isOrWasSubmitting = wasSubmitting.current || isSubmitting;
  const confirmationPending =
    requireConfirmationText !== undefined && confirmationInput !== requireConfirmationText;

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={true}>
      <DialogContent
        aria-describedby={undefined}
        onInteractOutside={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <p>{description}</p>

        {requireConfirmationText && (
          <div className="space-y-2">
            <label htmlFor="confirm-delete-input" className="text-sm text-muted-foreground">
              Type{" "}
              <span className="font-semibold text-foreground">
                "{requireConfirmationText}"
              </span>{" "}
              to confirm
            </label>
            <Input
              id="confirm-delete-input"
              data-testid="confirm-delete-input"
              value={confirmationInput}
              onChange={(e) => setConfirmationInput(e.target.value)}
              placeholder={requireConfirmationText}
              autoComplete="off"
            />
          </div>
        )}

        <DialogFooter>
          <div className="flex flex-row gap-2 justify-end">
            <Button variant="ghost" data-testid="cancel-button" onClick={() => setOpen(false)}>
              {cancelButtonText}
            </Button>
            <FormComponent method="post" action={action}>
              <CSRFToken />
              {idField && idFieldValue && (
                <input type="hidden" name={idField} value={idFieldValue} />
              )}
              {Object.entries(otherInputs).map(([key, value]) => (
                <input key={key} type="hidden" name={key} value={value} />
              ))}
              <Button
                data-testid="confirm-delete-button"
                variant="newPrimary"
                type="submit"
                name="subaction"
                value={subaction}
                disabled={isOrWasSubmitting || confirmationPending}
              >
                {isOrWasSubmitting ? submittingButtonText : continueButtonText}
              </Button>
            </FormComponent>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
